import * as React from 'react';
import { IgcTreeGridComponent as Component } from 'igniteui-webcomponents-grids/grids/index.js';
import type { IgcTreeGridComponentEventMap } from 'igniteui-webcomponents-grids/grids/index.js';
import { type EventName, createComponent } from '../react-props.js';

/**
 * **Ignite UI for Angular Tree Grid** -  [Documentation](https://www.infragistics.com/products/ignite-ui-angular/angular/components/grid/grid)   The Ignite UI Tree Grid displays and manipulates hierarchical data with consistent schema formatted as a table and  provides features such as sorting, filtering, editing, column pinning, paging, column moving and hiding.   Example:
 * @fires filteringExpressionsTreeChange - Emitted after filtering is performed.
 * @fires advancedFilteringExpressionsTreeChange - Emitted after advanced filtering is performed.
 * @fires gridScroll - Emitted when grid is scrolled horizontally/vertically.
 * @fires cellClick - Emitted when a cell is clicked.
 * @fires rowClick - Emitted when a row is clicked.
 * @fires formGroupCreated - Emitted when formGroup is created on edit of row/cell.
 * @fires validationStatusChange - Emitted when grid's validation status changes.
 * @fires selected - Emitted when a cell is selected.
 * @fires rowSelectionChanging - Emitted when `IgxGridRowComponent` is selected.
 * @fires columnSelectionChanging - Emitted when `IgxColumnComponent` is selected.
 * @fires columnPin - Emitted before `IgxColumnComponent` is pinned.
 * @fires columnPinned - Emitted after `IgxColumnComponent` is pinned.
 * @fires cellEditEnter - Emitted when cell enters edit mode.
 * @fires cellEditExit - Emitted when cell exits edit mode.
 * @fires cellEdit - Emitted when cell has been edited.
 * @fires cellEditDone - Emitted after cell has been edited and editing has been committed.
 * @fires rowEditEnter - Emitted when a row enters edit mode.
 * @fires rowEdit - Emitted when exiting edit mode for a row.
 * @fires rowEditDone - Emitted after exiting edit mode for a row and editing has been committed.
 * @fires rowEditExit - Emitted when row editing is canceled.
 * @fires columnInit - Emitted when a column is initialized.
 * @fires columnsAutogenerated -
 * @fires sorting - Emitted before sorting expressions are applied.
 * @fires sortingDone - Emitted after sorting is completed.
 * @fires filtering - Emitted before filtering expressions are applied.
 * @fires filteringDone - Emitted after filtering is performed through the UI.
 * @fires rowAdded - Emitted when a row is added.
 * @fires rowDeleted - Emitted when a row is deleted.
 * @fires rowDelete - Emmited when deleting a row.
 * @fires rowAdd - Emmited just before the newly added row is commited.
 * @fires columnResized - Emitted after column is resized.
 * @fires contextMenu - Emitted when a cell or row is right clicked.
 * @fires doubleClick - Emitted when a cell is double clicked.
 * @fires columnVisibilityChanging - Emitted before column visibility is changed.
 * @fires columnVisibilityChanged - Emitted after column visibility is changed.
 * @fires columnMovingStart - Emitted when column moving starts.
 * @fires columnMoving - Emitted during the column moving operation.
 * @fires columnMovingEnd - Emitted when column moving ends.
 * @fires gridKeydown - Emitted when keydown is triggered over element inside grid's body.
 * @fires rowDragStart - Emitted when start dragging a row.
 * @fires rowDragEnd - Emitted when dropping a row.
 * @fires gridCopy - Emitted when a copy operation is executed.
 * @fires expansionStatesChange - Emitted when the rows are expanded or collapsed.
 * @fires selectedRowsChange - Emitted when the rows are selected or deselected.
 * @fires rowToggle - Emitted when the expanded state of a row gets changed.
 * @fires rowPinning - Emitted when the pinned state of a row is changed.
 * @fires rowPinned - Emitted when the pinned state of a row is changed.
 * @fires activeNodeChange - Emitted when the active node is changed.
 * @fires sortingExpressionsChange - Emitted before sorting is performed.
 * @fires toolbarExporting - Emitted when an export process is initiated by the user.
 * @fires rangeSelected - Emitted when making a range selection.
 * @fires rendered - Emitted after the ngAfterViewInit hook. At this point the grid exists in the DOM
 * @fires dataChanging - Emitted before the grid's data view is changed because of a data operation, rebinding, etc.
 * @fires dataChanged - Emitted after the grid's data view is changed because of a data operation, rebinding, etc.
 * @class
 */
export const IgrTreeGrid = /* @__PURE__ */ createComponent({
  tagName: 'igc-tree-grid',
  displayName: 'IgrTreeGrid',
  elementClass: Component,
  react: React,
  moveBackOnDelete: true,
  events: {
    onFilteringExpressionsTreeChange:
      'filteringExpressionsTreeChange' as EventName<
        IgcTreeGridComponentEventMap['filteringExpressionsTreeChange']
      >,
    onAdvancedFilteringExpressionsTreeChange:
      'advancedFilteringExpressionsTreeChange' as EventName<
        IgcTreeGridComponentEventMap['advancedFilteringExpressionsTreeChange']
      >,
    onGridScroll: 'gridScroll' as EventName<
      IgcTreeGridComponentEventMap['gridScroll']
    >,
    onCellClick: 'cellClick' as EventName<
      IgcTreeGridComponentEventMap['cellClick']
    >,
    onRowClick: 'rowClick' as EventName<
      IgcTreeGridComponentEventMap['rowClick']
    >,
    onFormGroupCreated: 'formGroupCreated' as EventName<
      IgcTreeGridComponentEventMap['formGroupCreated']
    >,
    onValidationStatusChange: 'validationStatusChange' as EventName<
      IgcTreeGridComponentEventMap['validationStatusChange']
    >,
    onSelected: 'selected' as EventName<
      IgcTreeGridComponentEventMap['selected']
    >,
    onRowSelectionChanging: 'rowSelectionChanging' as EventName<
      IgcTreeGridComponentEventMap['rowSelectionChanging']
    >,
    onColumnSelectionChanging: 'columnSelectionChanging' as EventName<
      IgcTreeGridComponentEventMap['columnSelectionChanging']
    >,
    onColumnPin: 'columnPin' as EventName<
      IgcTreeGridComponentEventMap['columnPin']
    >,
    onColumnPinned: 'columnPinned' as EventName<
      IgcTreeGridComponentEventMap['columnPinned']
    >,
    onCellEditEnter: 'cellEditEnter' as EventName<
      IgcTreeGridComponentEventMap['cellEditEnter']
    >,
    onCellEditExit: 'cellEditExit' as EventName<
      IgcTreeGridComponentEventMap['cellEditExit']
    >,
    onCellEdit: 'cellEdit' as EventName<
      IgcTreeGridComponentEventMap['cellEdit']
    >,
    onCellEditDone: 'cellEditDone' as EventName<
      IgcTreeGridComponentEventMap['cellEditDone']
    >,
    onRowEditEnter: 'rowEditEnter' as EventName<
      IgcTreeGridComponentEventMap['rowEditEnter']
    >,
    onRowEdit: 'rowEdit' as EventName<IgcTreeGridComponentEventMap['rowEdit']>,
    onRowEditDone: 'rowEditDone' as EventName<
      IgcTreeGridComponentEventMap['rowEditDone']
    >,
    onRowEditExit: 'rowEditExit' as EventName<
      IgcTreeGridComponentEventMap['rowEditExit']
    >,
    onColumnInit: 'columnInit' as EventName<
      IgcTreeGridComponentEventMap['columnInit']
    >,
    onColumnsAutogenerated: 'columnsAutogenerated' as EventName<
      IgcTreeGridComponentEventMap['columnsAutogenerated']
    >,
    onSorting: 'sorting' as EventName<IgcTreeGridComponentEventMap['sorting']>,
    onSortingDone: 'sortingDone' as EventName<
      IgcTreeGridComponentEventMap['sortingDone']
    >,
    onFiltering: 'filtering' as EventName<
      IgcTreeGridComponentEventMap['filtering']
    >,
    onFilteringDone: 'filteringDone' as EventName<
      IgcTreeGridComponentEventMap['filteringDone']
    >,
    onRowAdded: 'rowAdded' as EventName<
      IgcTreeGridComponentEventMap['rowAdded']
    >,
    onRowDeleted: 'rowDeleted' as EventName<
      IgcTreeGridComponentEventMap['rowDeleted']
    >,
    onRowDelete: 'rowDelete' as EventName<
      IgcTreeGridComponentEventMap['rowDelete']
    >,
    onRowAdd: 'rowAdd' as EventName<IgcTreeGridComponentEventMap['rowAdd']>,
    onColumnResized: 'columnResized' as EventName<
      IgcTreeGridComponentEventMap['columnResized']
    >,
    onContextMenu: 'contextMenu' as EventName<
      IgcTreeGridComponentEventMap['contextMenu']
    >,
    onDoubleClick: 'doubleClick' as EventName<
      IgcTreeGridComponentEventMap['doubleClick']
    >,
    onColumnVisibilityChanging: 'columnVisibilityChanging' as EventName<
      IgcTreeGridComponentEventMap['columnVisibilityChanging']
    >,
    onColumnVisibilityChanged: 'columnVisibilityChanged' as EventName<
      IgcTreeGridComponentEventMap['columnVisibilityChanged']
    >,
    onColumnMovingStart: 'columnMovingStart' as EventName<
      IgcTreeGridComponentEventMap['columnMovingStart']
    >,
    onColumnMoving: 'columnMoving' as EventName<
      IgcTreeGridComponentEventMap['columnMoving']
    >,
    onColumnMovingEnd: 'columnMovingEnd' as EventName<
      IgcTreeGridComponentEventMap['columnMovingEnd']
    >,
    onGridKeydown: 'gridKeydown' as EventName<
      IgcTreeGridComponentEventMap['gridKeydown']
    >,
    onRowDragStart: 'rowDragStart' as EventName<
      IgcTreeGridComponentEventMap['rowDragStart']
    >,
    onRowDragEnd: 'rowDragEnd' as EventName<
      IgcTreeGridComponentEventMap['rowDragEnd']
    >,
    onGridCopy: 'gridCopy' as EventName<
      IgcTreeGridComponentEventMap['gridCopy']
    >,
    onExpansionStatesChange: 'expansionStatesChange' as EventName<
      IgcTreeGridComponentEventMap['expansionStatesChange']
    >,
    onSelectedRowsChange: 'selectedRowsChange' as EventName<
      IgcTreeGridComponentEventMap['selectedRowsChange']
    >,
    onRowToggle: 'rowToggle' as EventName<
      IgcTreeGridComponentEventMap['rowToggle']
    >,
    onRowPinning: 'rowPinning' as EventName<
      IgcTreeGridComponentEventMap['rowPinning']
    >,
    onRowPinned: 'rowPinned' as EventName<
      IgcTreeGridComponentEventMap['rowPinned']
    >,
    onActiveNodeChange: 'activeNodeChange' as EventName<
      IgcTreeGridComponentEventMap['activeNodeChange']
    >,
    onSortingExpressionsChange: 'sortingExpressionsChange' as EventName<
      IgcTreeGridComponentEventMap['sortingExpressionsChange']
    >,
    onToolbarExporting: 'toolbarExporting' as EventName<
      IgcTreeGridComponentEventMap['toolbarExporting']
    >,
    onRangeSelected: 'rangeSelected' as EventName<
      IgcTreeGridComponentEventMap['rangeSelected']
    >,
    onRendered: 'rendered' as EventName<
      IgcTreeGridComponentEventMap['rendered']
    >,
    onDataChanging: 'dataChanging' as EventName<
      IgcTreeGridComponentEventMap['dataChanging']
    >,
    onDataChanged: 'dataChanged' as EventName<
      IgcTreeGridComponentEventMap['dataChanged']
    >,
  },
  renderProps: {
    rowLoadingIndicatorTemplate: 'rowLoadingIndicatorTemplate',
    emptyGridTemplate: 'emptyGridTemplate',
    addRowEmptyTemplate: 'addRowEmptyTemplate',
    loadingGridTemplate: 'loadingGridTemplate',
    dragGhostCustomTemplate: 'dragGhostCustomTemplate',
    rowEditTextTemplate: 'rowEditTextTemplate',
    rowAddTextTemplate: 'rowAddTextTemplate',
    rowEditActionsTemplate: 'rowEditActionsTemplate',
    rowExpandedIndicatorTemplate: 'rowExpandedIndicatorTemplate',
    rowCollapsedIndicatorTemplate: 'rowCollapsedIndicatorTemplate',
    headerExpandedIndicatorTemplate: 'headerExpandedIndicatorTemplate',
    headerCollapsedIndicatorTemplate: 'headerCollapsedIndicatorTemplate',
    excelStyleHeaderIconTemplate: 'excelStyleHeaderIconTemplate',
    sortAscendingHeaderIconTemplate: 'sortAscendingHeaderIconTemplate',
    sortDescendingHeaderIconTemplate: 'sortDescendingHeaderIconTemplate',
    sortHeaderIconTemplate: 'sortHeaderIconTemplate',
    headSelectorTemplate: 'headSelectorTemplate',
    rowSelectorTemplate: 'rowSelectorTemplate',
    dragIndicatorIconTemplate: 'dragIndicatorIconTemplate',
  },
});

export type IgrTreeGrid = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrTreeGridModule = Component;
